import { NftCollectionAdminRow } from "@components/admin/NftCollectionAdminRow";
import { SocialHead } from "@components/all-pages/SocialHead";
import { GlowButton } from "@components/atoms/GlowButton";
import { ChartContainer } from "@components/charts/ChartContainer";
import { SolanaPage } from "@components/nav/SolanaPage";
import { CollectionAttribute } from "@components/nft/CollectionAttribute";
import { CollectionNftGrid } from "@components/nft/CollectionNftGrid";
import {
  CollectionPageProvider,
  NftCollectionTab,
  useCollectionPageContext,
} from "@components/nft/CollectionPageContext";
import { CollectionSales } from "@components/nft/CollectionSales";
import { getGlowImageUrl, GlowGetApi, Solana } from "@glow/shared";
import { LuxLink } from "@lux/atoms/components/LuxLink";
import { SparkChart } from "@glow/react/components/charts/SparkChart";
import { SquareNftImage } from "@glow/react/components/nfts/SquareNftImage";
import MagicEdenIcon from "@glow/react/icons/MagicEden.svg";
import { ResponsiveBreakpoint } from "@utils/StyleConstants";
import { ZmClient } from "@utils/ZmClient";
import classNames from "classnames";
import { GetServerSideProps } from "next";
import { useRouter } from "next/router";
import { solAmountString } from "@glow/crypto";

export default function SolanaNfts(
  initialData: GlowGetApi["/solana/explore/get-nft-collection-info"]["response"],
) {
  const router = useRouter();
  const [nftCollectionSymbol] = router.query.nftCollectionSymbol as string[];

  return (
    <CollectionPageProvider
      nftCollectionSymbol={nftCollectionSymbol}
      initialData={initialData}
    >
      <CollectionPageInner />
    </CollectionPageProvider>
  );
}

const CollectionPageInner = () => {
  const router = useRouter();
  const { data, mutate, pricePoints, activeTab, emptyCollectionFilters } =
    useCollectionPageContext();
  const [nftCollectionSymbol] = router.query.nftCollectionSymbol as string[];

  if (!data) {
    return <SolanaPage title="Loading...">Loading...</SolanaPage>;
  }

  const collection = data.collection;

  return (
    <SolanaPage title={data.collection.name || "Loading..."}>
      <SocialHead
        subtitle={collection.name}
        description={collection.description!}
        canonicalPath={`/collection/${collection.magic_eden_symbol}`}
        twitterCardType={"summary"}
        imageUrl={
          collection.image_url
            ? getGlowImageUrl({
                url: collection.image_url,
                width: 500,
                height: 500,
              })
            : null
        }
        imageSize={{ width: 500, height: 500 }}
      />

      <div
        className={classNames("page-wrapper", {
          "empty-filters": emptyCollectionFilters,
        })}
      >
        <div className="top-banner">
          <div
            className="banner-inner"
            style={
              data.collection.image_url
                ? {
                    backgroundImage: `url(${getGlowImageUrl({
                      url: data.collection.image_url,
                      width: 64,
                      height: 64,
                    })})`,
                  }
                : {}
            }
          />
        </div>

        <div className="zm-container">
          <div className="top-icon">
            <SquareNftImage
              imageUrl={data.collection.image_url}
              name={data.collection.name}
              size={64}
              rounded
            />
          </div>

          <div className="name-section">
            <h1>{data.collection.name}</h1>
          </div>

          {data.collection.network === Solana.Network.Mainnet && (
            <>
              <div className="flex-center stats my-4 flex-wrap">
                <CollectionAttribute
                  label="30d Volume"
                  value={solAmountString(data.collection.volume_30d_sol!)}
                />
                <div className="flex-center stats-price">
                  <CollectionAttribute
                    label="30d Avg Price"
                    value={solAmountString(data.collection.avg_price_30d_sol!)}
                  />
                  {pricePoints && pricePoints?.length > 3 && (
                    <LuxLink
                      href={`/collection/${nftCollectionSymbol}/sales`}
                      className={"block"}
                    >
                      <ChartContainer>
                        {({ width }) => (
                          <SparkChart
                            width={width}
                            height={45}
                            points={pricePoints!}
                          />
                        )}
                      </ChartContainer>
                    </LuxLink>
                  )}
                </div>
              </div>
            </>
          )}

          {Boolean(data && router.query.admin) && (
            <NftCollectionAdminRow
              collection={data.collection}
              refresh={mutate}
            />
          )}

          <div className="desc my-4">{data.collection.description}</div>

          {data.collection.magic_eden_symbol &&
            data.collection.network === Solana.Network.Mainnet && (
              <div className="flex-center my-4">
                <GlowButton
                  href={`https://magiceden.io/marketplace/${data.collection.magic_eden_symbol}`}
                  color="light"
                  icon={<MagicEdenIcon />}
                  label="Magic Eden"
                  size="small"
                  className="mr-2"
                  rounded
                />
              </div>
            )}
        </div>

        <div className="content-outer-wrapper">
          {activeTab === NftCollectionTab.Nfts && <CollectionNftGrid />}
          {activeTab === NftCollectionTab.Sales && <CollectionSales />}
        </div>
      </div>

      <style jsx>{`
        .page-wrapper {
          --max-width: 1000px;
          --nft-max-width: 1584px;
          --nft-right-padding: calc(296px + var(--side-padding));

          @media (max-width: 1584px) {
            --max-width: 1200px;
            --nft-max-width: 1200px;
            --nft-right-padding: var(--side-padding);
          }

          // If there are no filters to show,
          // increase the content width to larger size
          &.empty-filters {
            --max-width: 1200px;

            .content-outer-wrapper {
              padding-right: var(--side-padding);
              max-width: var(--max-width);
            }
          }
        }

        .name-section {
          display: flex;
          justify-content: space-between;

          @media (max-width: ${ResponsiveBreakpoint.medium}) {
            display: block;
            h1 {
              margin-bottom: 1rem;
            }
          }
        }

        .top-banner {
          height: 100px;
          overflow: hidden;

          .banner-inner {
            width: calc(100% + 64px);
            height: calc(100% + 64px);
            margin-left: -32px;
            margin-top: -32px;
            background-color: gray;
            filter: blur(2rem);
            background-size: cover;
            background-position: center;
          }
        }

        .top-icon {
          display: inline-flex;
          border-radius: var(--border-radius);
          overflow: hidden;
          position: relative;
          margin-top: -34px;
          margin-bottom: 1rem;

          :global(> div) {
            border: 4px solid var(--primary-bg-color);
            background-color: var(--secondary-bg-color);
          }
        }

        .zm-container {
          padding: 0 var(--side-padding);
        }

        h1 {
          margin: 0;
          margin-right: 1rem;
        }

        .desc {
          max-width: 800px;
        }

        .stats {
          :global(.collection-attribute:not(:last-child)) {
            margin-right: 2rem;
          }
        }

        @media (max-width: ${ResponsiveBreakpoint.small}) {
          .stats {
            width: 100%;

            .stats-price {
              margin-top: 1rem;
              width: 100%;
            }

            :global(.collection-attribute) {
              min-width: 6rem;
            }
          }
        }

        @media (max-width: ${ResponsiveBreakpoint.tiny}) {
          .desc {
            font-size: var(--font-size-sm);
          }
        }

        .content-outer-wrapper {
          max-width: var(--nft-max-width);
          margin: 0 auto;
          padding: 1.5rem var(--side-padding);
          padding-right: var(--nft-right-padding);
        }
      `}</style>
    </SolanaPage>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  try {
    const [nftCollectionSymbol] = ctx.query.nftCollectionSymbol as string[];
    const data = await ZmClient.get("/solana/explore/get-nft-collection-info", {
      magic_eden_symbol: nftCollectionSymbol,
      network: ctx.query.network as Solana.Network | undefined,
    });
    return { props: data };
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return { notFound: true };
    }
    return { props: { errorCode: error?.response?.status || 500 } };
  }
};
